import React, { Suspense, useMemo, useReducer, useEffect, useCallback, useRef } from "react";
import type { FC, Dispatch } from "react";

import {
  DatePickerModal,
  DateTimePickerModal,
  DayTimePickerModal,
} from "@pianofunclub/react-native-paper-dates";
import { format, endOfWeek, startOfWeek } from "date-fns";
import {
  Box,
  Text,
  VStack,
  Button,
  Pressable,
  Avatar,
  useToast,
  useDisclose,
  PresenceTransition,
  HStack,
  CloseIcon,
  Center,
  Modal,
  Checkbox,
} from "native-base";
import { useBeforeunload } from "react-beforeunload";
import { Platform } from "react-native";
import { useQueryLoader, usePreloadedQuery, useMutation } from "react-relay";
import type { PreloadedQuery } from "react-relay";
import type { RecordSourceSelectorProxy } from "relay-runtime";

import DbsStatus from "pianofunclub-crm/components/Accounts/DbsStatus";
import SideBar from "pianofunclub-crm/components/Drawers/SideBar";
import ItemStatus from "pianofunclub-crm/components/General/ItemStatus";
import InvoiceAndEmail from "pianofunclub-crm/components/Invoicing/InvoiceAndEmail";
import CreateAccountModal from "pianofunclub-crm/components/Modals/CreateAccountModal";
import CreateBreakBlockModal from "pianofunclub-crm/components/Modals/CreateBreakBlockModal";
import UpdateLessonBlockTeacherOrPupilModal from "pianofunclub-crm/components/Modals/UpdateLessonBlockTeacherOrPupilModal";
import PupilRegisters from "pianofunclub-crm/components/Tabs/PupilRegisters";
import type {
  AccountsStackNavigatorProps,
  AccountsStackRouteProps,
} from "pianofunclub-crm/navigation/AccountsNavigator";
import type {
  InvoicingStackNavigatorProps,
  InvoicingStackRouteProps,
} from "pianofunclub-crm/navigation/InvoicingNavigator";
import type {
  PayrollStackNavigatorProps,
  PayrollStackRouteProps,
} from "pianofunclub-crm/navigation/PayrollNavigator";
import type {
  RegistersStackNavigatorProps,
  RegistersStackRouteProps,
} from "pianofunclub-crm/navigation/RegistersNavigator";
import type {
  SchoolsStackNavigatorProps,
  SchoolsStackRouteProps,
} from "pianofunclub-crm/navigation/SchoolsNavigator";
import type {
  WaitingListStackNavigatorProps,
  WaitingListStackRouteProps,
} from "pianofunclub-crm/navigation/WaitingListNavigator";

import LoadingBlobs from "pianofunclub-shared/components/Animations/LoadingBlobs";
import ButtonDebounced from "pianofunclub-shared/components/Buttons/ButtonDebounced";
import TopTabBar from "pianofunclub-shared/components/Buttons/TopTabBar";
import PercentageInput from "pianofunclub-shared/components/Inputs/PercentageInput";
import TextInput from "pianofunclub-shared/components/Inputs/TextInput";
import type { TextInputRef } from "pianofunclub-shared/components/Inputs/TextInput";
import ListEmptyBanner from "pianofunclub-shared/components/ListItems/ListEmptyBanner";
import type { RegisterUpdate } from "pianofunclub-shared/components/Modals/ManageRegisterLessonModal";
import PupilsToArchiveModal from "pianofunclub-shared/components/Modals/PupilsToArchiveModal";
import type { PupilsToArchiveModalInfo } from "pianofunclub-shared/components/Modals/PupilsToArchiveModal";
import UpdateLessonBlockDetailsModal from "pianofunclub-shared/components/Modals/UpdateLessonBlockDetailsModal";
import type {
  ShowUpdateBlockDetailsModalHandler,
  UpdateBlockDetailsModalInfo,
} from "pianofunclub-shared/components/Modals/UpdateLessonBlockDetailsModal";
import Actionsheet from "pianofunclub-shared/components/NativeBaseExtended/Actionsheet";
import AlertPopup from "pianofunclub-shared/components/NativeBaseExtended/AlertPopup";
import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import {
  ArchivedIcon,
  CompleteIcon,
  ProfileIcon,
  TickIcon,
  TrashIcon,
  WarningIcon,
} from "pianofunclub-shared/components/Other/Icons";
import TeacherRegisters from "pianofunclub-shared/components/Registers/TeacherRegisters";
import type {
  RegisterProgress,
  TeacherRegistersReducerValues,
} from "pianofunclub-shared/components/Registers/TeacherRegisters";
import TeacherTimetable from "pianofunclub-shared/components/Registers/TeacherTimetable";
import type { TeacherTimetableReducerValues } from "pianofunclub-shared/components/Registers/TeacherTimetable";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import { useData } from "pianofunclub-shared/providers/DataProvider";
import type {
  EditProfileMutation,
  EditProfileMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/EditProfileMutation.graphql";
import type { LoadAccountQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadAccountQuery.graphql";
import type {
  RemoveParentRelationshipMutation,
  RemoveParentRelationshipMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/RemoveParentRelationshipMutation.graphql";
import type {
  UpdateAccountStatusMutation,
  UpdateAccountStatusMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/UpdateAccountStatusMutation.graphql";
import { edit_profile } from "pianofunclub-shared/relay/graphql/accounts/EditProfile";
import { load_account } from "pianofunclub-shared/relay/graphql/accounts/LoadAccount";
import { remove_parent_relationship } from "pianofunclub-shared/relay/graphql/accounts/RemoveParentRelationship";
import { update_account_status } from "pianofunclub-shared/relay/graphql/accounts/UpdateAccountStatus";
import type { LoadInvoiceQuery } from "pianofunclub-shared/relay/graphql/invoicing/__generated__/LoadInvoiceQuery.graphql";
import { load_invoice } from "pianofunclub-shared/relay/graphql/invoicing/LoadInvoice";
import type {
  AddNewLessonBlockMutation,
  AddNewLessonBlockMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/AddNewLessonBlockMutation.graphql";
import type { LoadLessonBlocksQuery } from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadLessonBlocksQuery.graphql";
import type { LoadLessonsQuery } from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadLessonsQuery.graphql";
import type {
  RemoveLessonBlocksMutation,
  RemoveLessonBlocksMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/RemoveLessonBlocksMutation.graphql";
import type {
  RescheduleLessonMutation,
  RescheduleLessonMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/RescheduleLessonMutation.graphql";
import type {
  UpdateLessonBlockDetailsMutation,
  UpdateLessonBlockDetailsMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/UpdateLessonBlockDetailsMutation.graphql";
import type {
  UpdateRegistersMutation,
  UpdateRegistersMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/UpdateRegistersMutation.graphql";
import type {
  UpdateScheduledLessonBlockTimeMutation,
  UpdateScheduledLessonBlockTimeMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/UpdateScheduledLessonBlockTimeMutation.graphql";
import { add_new_lesson_block } from "pianofunclub-shared/relay/graphql/registers/AddNewLessonBlock";
import { load_lesson_blocks } from "pianofunclub-shared/relay/graphql/registers/LoadLessonBlocks";
import { load_lessons } from "pianofunclub-shared/relay/graphql/registers/LoadLessons";
import { remove_lesson_blocks } from "pianofunclub-shared/relay/graphql/registers/RemoveLessonBlocks";
import { reschedule_lesson } from "pianofunclub-shared/relay/graphql/registers/RescheduleLesson";
import { update_lesson_block_details } from "pianofunclub-shared/relay/graphql/registers/UpdateLessonBlockDetails";
import { update_registers } from "pianofunclub-shared/relay/graphql/registers/UpdateRegisters";
import { update_scheduled_lesson_block_time } from "pianofunclub-shared/relay/graphql/registers/UpdateScheduledLessonBlockTime";
import { lessonBlockConnectionUpdater } from "pianofunclub-shared/relay/updaters";
import { customColors } from "pianofunclub-shared/theme";
import type { AccountType } from "pianofunclub-shared/types";
import { DISCOUNT_CATEGORIES, SCHOOL_YEARS } from "pianofunclub-shared/utils/constants";
import {
  titleCaseConverter,
  getInitials,
  schoolYearConverter,
  isNumeric,
} from "pianofunclub-shared/utils/converters";
import {
  getCurrentBlock,
  getFullName,
  getDefaultStartingYearAndStartingYearsForSelect,
} from "pianofunclub-shared/utils/extractors";
import { ensureDateType } from "pianofunclub-shared/utils/helpers";
import { createReducer } from "pianofunclub-shared/utils/reducers";
import type { State, Action } from "pianofunclub-shared/utils/reducers";
import { isNavigationAction } from "pianofunclub-shared/utils/typeGuards";

export type NavigationProps = AccountsStackNavigatorProps<"Account"> &
  InvoicingStackNavigatorProps<"Account"> &
  SchoolsStackNavigatorProps<"Account"> &
  RegistersStackNavigatorProps<"Account"> &
  WaitingListStackNavigatorProps<"Account"> &
  PayrollStackNavigatorProps<"Account">;

export type RouteProps = AccountsStackRouteProps<"Account"> &
  InvoicingStackRouteProps<"Account"> &
  SchoolsStackRouteProps<"Account"> &
  RegistersStackRouteProps<"Account"> &
  WaitingListStackRouteProps<"Account"> &
  PayrollStackRouteProps<"Account">;

interface ScreenProps {
  navigation: NavigationProps;
  route: RouteProps;
}

interface ContentProps {
  accountType: AccountType;
  dispatchState: Dispatch<Action<ReducerValues>>;
  loadAccountQueryReference: PreloadedQuery<LoadAccountQuery, Record<string, unknown>>;
  navigation: NavigationProps;
  route: RouteProps;
  startingYears: {
    label: string;
    value: number;
  }[];
  state: State<ReducerValues>;
}

export interface ReducerValues
  extends TeacherRegistersReducerValues,
    TeacherTimetableReducerValues {
  addBreakBlockModalIsOpen?: boolean;
  addLessonBlockModalIsOpen?: boolean;
  clickedAccountId?: string;
  createAccountModalIsOpen?: boolean;
  dbsExpiryDatePickerIsOpen?: boolean;
  deleteAccountAlertIsOpen?: boolean;
  discountCategorySelect?: string;
  discountPercentage?: number;
  hasBook?: boolean;
  hasInstrument?: boolean;
  hasReturnedInstrument?: boolean;
  hours?: number;
  instrumentSuppliedByUs?: boolean;
  invoiceId?: string;
  lessonBlockActionsheetId?: string;
  minutes?: number;
  paidBySchoolSelect?: boolean;
  pupilsToArchiveModalInfo?: PupilsToArchiveModalInfo;
  refreshHandler?: () => void;
  registerUpdater?: number;
  removeParentRelationshipAlertIsOpen?: boolean;
  // don't handle state for most text inputs (mutation is fired on blur)
  // do for the selects so that they respond immeditately
  schoolIdSelect?: string;
  schoolYearSelect?: string;
  sideBarIsCollapsed?: boolean;
  updateBlockDetailsModalInfo?: UpdateBlockDetailsModalInfo;
}

export const LOAD_X_LESSON_BLOCKS = Platform.OS === "web" ? 20 : 10;
const SIDE_BAR_EXPANDED_WIDTH = 400;
const SIDE_BAR_COLLAPSED_WIDTH = 12;

const AccountContent: FC<ContentProps> = (props) => {
  const {
    accountType,
    dispatchState,
    loadAccountQueryReference,
    navigation,
    route,
    startingYears,
    state,
  } = props;

  const accountData = usePreloadedQuery(load_account, loadAccountQueryReference);

  useEffect(() => {
    if (accountData.profile?.id) {
      // initialise local state
      // day index defaults to current day, but adjust if teacher
      // doesn't teach on current day
      dispatchState({
        input: "dayIndex",
        value:
          accountType === "TEACHER" &&
          accountData.profile.teachingDays &&
          accountData.profile.teachingDays.length > 0 &&
          !accountData.profile.teachingDays.includes(state.values.dayIndex)
            ? (accountData.profile.teachingDays[0] ?? state.values.dayIndex)
            : state.values.dayIndex,
      });
      dispatchState({
        input: "teachingDays",
        value: accountData.profile.teachingDays ?? [],
      });
      dispatchState({
        input: "schoolIdSelect",
        value: accountData.profile.school?.id,
      });
      dispatchState({
        input: "schoolYearSelect",
        value: String(accountData.profile.schoolYear ?? undefined),
      });
      dispatchState({
        input: "paidBySchoolSelect",
        value: accountData.profile.paidBySchool !== null ? accountData.profile.paidBySchool : false,
      });
      dispatchState({
        input: "discountCategorySelect",
        value: String(accountData.profile.discountCategory ?? "FULL_PRICE"),
      });
      dispatchState({
        input: "discountPercentage",
        value: isNumeric(accountData.profile.discountPercentage)
          ? Number(accountData.profile.discountPercentage)
          : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData.profile, accountType]);

  const childrenConnections = useMemo(() => {
    return accountData.profile?.children.__id;
  }, [accountData.profile?.children.__id]);

  const [loadLessonBlocksQueryReference, loadLessonBlocksQuery] =
    useQueryLoader<LoadLessonBlocksQuery>(load_lesson_blocks);

  useEffect(() => {
    if (!state.values.contentIsRendered) {
      loadLessonBlocksQuery(
        {
          block: accountType === "TEACHER" ? state.values.block : undefined,
          dayIndex: state.values.dayIndex,
          first: LOAD_X_LESSON_BLOCKS,
          orderBy: accountType === "TEACHER" ? undefined : "-block,lessonDay,lessonTime",
          profileId: route.params.profileId,
          schoolId: state.values.schoolId,
          searchTerm: state.values.searchTerm,
          skipLessonBlocks: accountType !== "TEACHER" && accountType !== "PUPIL",
          skipLessonStages: true,
          skipRegisterProgress: accountType !== "TEACHER",
          startingYear: state.values.startingYear,
        },
        { fetchPolicy: "store-or-network" }
      );
    }
  }, [
    accountType,
    loadLessonBlocksQuery,
    route.params.profileId,
    state.values.block,
    state.values.contentIsRendered,
    state.values.dayIndex,
    state.values.schoolId,
    state.values.searchTerm,
    state.values.startingYear,
  ]);

  const [loadLessonsQueryReference, loadLessonsQuery] =
    useQueryLoader<LoadLessonsQuery>(load_lessons);

  useEffect(() => {
    if (!state.values.contentIsRendered) {
      let startDate = state.values.timetableDate ?? new Date();
      let endDate;
      if (state.values.timetableView === "WEEK") {
        startDate = startOfWeek(startDate, { weekStartsOn: 1 });
        endDate = endOfWeek(startDate, { weekStartsOn: 1 });
      }

      loadLessonsQuery(
        {
          endDate: endDate?.toString(),
          orderBy: "rearrangedTimestamp,scheduledTimestamp",
          profileId: route.params.profileId,
          skip: accountType !== "TEACHER",
          startDate: startDate.toString(),
        },
        { fetchPolicy: "store-or-network" }
      );
    }
  }, [
    accountType,
    loadLessonsQuery,
    route.params.profileId,
    state.values.contentIsRendered,
    state.values.timetableDate,
    state.values.timetableView,
  ]);

  const [loadInvoiceQueryReference, loadInvoiceQuery] =
    useQueryLoader<LoadInvoiceQuery>(load_invoice);

  useEffect(() => {
    if (!state.values.contentIsRendered) {
      loadInvoiceQuery(
        {
          block: state.values.block,
          profileId: route.params.profileId,
          skip: accountType !== "PARENT",
          startingYear: state.values.startingYear,
        },
        { fetchPolicy: "store-or-network" }
      );
    }
  }, [
    accountType,
    loadInvoiceQuery,
    route.params.profileId,
    state.values.block,
    state.values.contentIsRendered,
    state.values.startingYear,
  ]);

  const { dataState } = useData();

  const allSchools = useMemo(() => {
    return dataState.values.schools?.edges ?? [];
  }, [dataState.values.schools?.edges]);

  const teacherSchools = useMemo(() => {
    return accountData?.profile?.schools?.toSorted(
      (a, b) => a?.name.localeCompare(b?.name ?? "") ?? 0
    );
  }, [accountData?.profile?.schools]);

  const pages = useMemo(() => {
    switch (accountType) {
      case "PARENT":
        return ["Invoices", "Notes"];
      case "PUPIL":
        return ["Registers", "Homework"];
      case "TEACHER":
        return ["Registers", "Timetable"];
      default:
        return ["Activity", "Notes"];
    }
  }, [accountType]);

  const safeLeaveRef = useRef(null);
  const safeRemoveRelationshipRef = useRef(null);
  const safeDeleteAccountRef = useRef(null);
  // use refs rather than state to track these, so that updates work properly
  // when run from nested children
  const registerUpdates = useRef<RegisterUpdate[]>([]);
  const registerProgressCounter = useRef<RegisterProgress>({
    marked: undefined,
    total: undefined,
  });
  const phoneNumberInputRef = useRef<TextInputRef>(null);

  // if unsaved updates have been made to registers, show an alert
  useEffect(() => {
    const unsubscribe = navigation.addListener("beforeRemove", (e) => {
      if (registerUpdates.current.length === 0) {
        return;
      }

      e.preventDefault();
      dispatchState({
        input: "leaveAlertAction",
        value: e.data.action,
      });
    });

    return () => unsubscribe();
  }, [dispatchState, navigation, registerUpdates.current.length]);

  // trigger a popup if the CRM is unloaded
  useBeforeunload((e) => {
    if (registerUpdates.current.length === 0) {
      return;
    }

    e.preventDefault();
  });

  const toast = useToast();

  const [commitUpdateRegisters, updateRegistersInFlight] =
    useMutation<UpdateRegistersMutation>(update_registers);

  const updateRegisters = useCallback(
    (
      variables: {
        lessonId: string;
        registerNote?: string;
        status: string;
      }[],
      onComplete?: () => void
    ) => {
      const updateRegistersConfig = {
        variables: {
          input: { updates: variables },
        },
        onCompleted: (response: UpdateRegistersMutation$data) => {
          if (response.updateRegisters?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert id={id} status="success" title={"Register updated"} toast={toast} />
              ),
            });
            onComplete?.();
            // delay to next render cycle for clean exit animation of save button
            setTimeout(() => {
              registerUpdates.current = [];
              dispatchState({
                input: "registerUpdater",
                value: Math.random(),
              });
            }, 0);
            if (response.updateRegisters?.pupilsToArchive?.length) {
              dispatchState({
                input: "pupilsToArchiveModalInfo",
                value: {
                  ids: response.updateRegisters.pupilsToArchive
                    .map((pupil) => pupil?.id)
                    .filter((item) => item !== undefined),
                  pupilNames: response.updateRegisters.pupilsToArchive.map((pupil) =>
                    getFullName(pupil?.user?.firstName, pupil?.user?.lastName)
                  ),
                },
              });
            }
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't update register"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitUpdateRegisters(updateRegistersConfig);
    },
    [commitUpdateRegisters, dispatchState, toast]
  );

  const commitRescheduleLesson = useMutation<RescheduleLessonMutation>(reschedule_lesson)[0];

  const rescheduleLesson = useCallback(
    (lessonId: string, rearrangedDatetime: Date, lessonDuration?: number) => {
      const rescheduleLessonConfig = {
        variables: {
          input: {
            lessonDuration: lessonDuration,
            lessonId: lessonId,
            rearrangedDatetime: String(rearrangedDatetime),
          },
        },
        onCompleted: (response: RescheduleLessonMutation$data) => {
          if (response?.rescheduleLesson?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert id={id} status="success" title={"Rescheduled lesson"} toast={toast} />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't reschedule lesson"}
                  toast={toast}
                />
              ),
            });
          }
        },
        optimsticResponse: {
          rescheduleLesson: {
            errors: null,
            lesson: {
              id: lessonId,
              rearrangedTimestamp: rearrangedDatetime,
            },
            success: true,
          },
        },
      };

      commitRescheduleLesson(rescheduleLessonConfig);
    },
    [commitRescheduleLesson, toast]
  );

  const [commitUpdateLessonBlockDetails, updateLessonBlockDetailsInFlight] =
    useMutation<UpdateLessonBlockDetailsMutation>(update_lesson_block_details);

  const updateLessonBlockDetails = useCallback(
    (
      variables: {
        instrument?: string | null;
        keepFree?: boolean;
        lessonBlockIds: string[];
        pupilId?: string | null;
        staffNoteForTeacher?: string;
        stageId?: string | null;
        teacherId?: string | null;
        teacherNoteForStaff?: string;
      },
      lessonBlockCursor?: string,
      onComplete?: () => void
    ) => {
      const updateLessonBlockDetailsConfig = {
        variables: {
          block: state.values.block,
          connections: state.values.lessonBlocksConnectionId
            ? [state.values.lessonBlocksConnectionId]
            : [],
          input: variables,
          startingYear: state.values.startingYear,
        },
        onCompleted: (response: UpdateLessonBlockDetailsMutation$data) => {
          if (response?.updateLessonBlockDetails?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={`Updated lesson block${variables.lessonBlockIds.length > 1 ? "s" : ""}`}
                  toast={toast}
                />
              ),
            });
            onComplete?.();
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={`Couldn't update lesson block${
                    variables.lessonBlockIds.length > 1 ? "s" : ""
                  }`}
                  toast={toast}
                />
              ),
            });
          }
        },
        updater: (store: RecordSourceSelectorProxy) =>
          lessonBlockConnectionUpdater(
            store,
            state.values.lessonBlocksConnectionId,
            lessonBlockCursor
          ),
      };

      commitUpdateLessonBlockDetails(updateLessonBlockDetailsConfig);
    },
    [
      commitUpdateLessonBlockDetails,
      state.values.block,
      state.values.lessonBlocksConnectionId,
      state.values.startingYear,
      toast,
    ]
  );

  const [commitUpdateScheduledLessonBlockTime, updateScheduledLessonBlockTimeInFlight] =
    useMutation<UpdateScheduledLessonBlockTimeMutation>(update_scheduled_lesson_block_time);

  const updateScheduledLessonBlockTime = useCallback(
    (
      lessonBlockIds: string[],
      dayIndex: number,
      hours?: number,
      minutes?: number,
      onComplete?: () => void
    ) => {
      const updateScheduledLessonBlockTimeConfig = {
        variables: {
          block: state.values.block,
          input: {
            dayIndex,
            hours,
            lessonBlockIds,
            minutes,
          },
          startingYear: state.values.startingYear,
        },
        onCompleted: (response: UpdateScheduledLessonBlockTimeMutation$data) => {
          if (response?.updateScheduledLessonBlockTime?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={`Rescheduled lesson block slot${lessonBlockIds.length > 1 ? "s" : ""}`}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={`Couldn't reschedule lesson block slot${
                    lessonBlockIds.length > 1 ? "s" : ""
                  }`}
                  toast={toast}
                />
              ),
            });
          }
          onComplete?.();
        },
      };

      commitUpdateScheduledLessonBlockTime(updateScheduledLessonBlockTimeConfig);
    },
    [commitUpdateScheduledLessonBlockTime, state.values.block, state.values.startingYear, toast]
  );

  const [commitRemoveLessonBlocks, removeLessonBlocksInFlight] =
    useMutation<RemoveLessonBlocksMutation>(remove_lesson_blocks);

  const removeLessonBlocks = useCallback(
    (lessonBlockIds: string[], connectionId?: string, onComplete?: () => void) => {
      const removeLessonBlocksConfig = {
        variables: {
          block: state.values.block,
          connections: connectionId ? [connectionId] : [],
          input: {
            isTeacherRegister: true,
            lessonBlockIds: lessonBlockIds,
          },
          startingYear: state.values.startingYear,
        },
        onCompleted: (response: RemoveLessonBlocksMutation$data) => {
          if (response?.removeLessonBlocks?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="error"
                  title={`Deleted lesson block${lessonBlockIds.length > 1 ? "s" : ""}`}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={`Couldn't delete lesson block${lessonBlockIds.length > 1 ? "s" : ""}`}
                  toast={toast}
                />
              ),
            });
          }
          onComplete?.();
        },
      };

      commitRemoveLessonBlocks(removeLessonBlocksConfig);
    },
    [commitRemoveLessonBlocks, state.values.block, state.values.startingYear, toast]
  );

  const [commitAddNewLessonBlock, addNewLessonBlockInFlight] =
    useMutation<AddNewLessonBlockMutation>(add_new_lesson_block);

  const addNewLessonBlock = useCallback(
    (variables: {
      block: number;
      dayIndex: number;
      durationMinutes?: number;
      hours: number;
      minutes: number;
      schoolId: string;
      startingYear: number;
      type: string;
    }) => {
      const addNewLessonBlockConfig = {
        variables: {
          connections: state.values.lessonBlocksConnectionId
            ? [state.values.lessonBlocksConnectionId]
            : [],
          input: {
            teacherId: route.params.profileId,
            ...variables,
          },
        },
        onCompleted: (response: AddNewLessonBlockMutation$data) => {
          dispatchState({
            input: "addLessonBlockModalIsOpen",
            value: false,
          });
          if (response?.addNewLessonBlock?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"Added new lesson block"}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't add new lesson block"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitAddNewLessonBlock(addNewLessonBlockConfig);
    },
    [
      commitAddNewLessonBlock,
      dispatchState,
      route.params.profileId,
      state.values.lessonBlocksConnectionId,
      toast,
    ]
  );

  const commitEditProfile = useMutation<EditProfileMutation>(edit_profile)[0];

  const editProfile = useCallback(
    (variables: {
      addressFirstLine?: string;
      addressSecondLine?: string;
      city?: string;
      dbsExpirationDate?: string;
      dbsNumber?: string;
      discountCategory?: string;
      discountPercentage?: number;
      email?: string;
      firstName?: string;
      hasBook?: boolean;
      hasInstrument?: boolean;
      instrumentSuppliedByUs?: boolean;
      lastName?: string;
      paidBySchool?: boolean;
      phoneNumber?: string;
      postcode?: string;
      profileId: string;
      schoolClass?: string;
      schoolId?: string;
      schoolYear?: number;
      staffNoteForTeacher?: string;
    }) => {
      const editProfileConfig = {
        variables: {
          input: variables,
        },
        onCompleted: (response: EditProfileMutation$data) => {
          // the phone number validator formats phone numbers
          // so update the text input to show the formatted phone number
          if (variables.phoneNumber && response.editProfile?.profile?.phoneNumber) {
            phoneNumberInputRef.current?.setValue(response.editProfile?.profile?.phoneNumber, true);
          }
          if (!response?.editProfile?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't update profile"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitEditProfile(editProfileConfig);
    },
    [commitEditProfile, toast]
  );

  const [commitRemoveParentRelationship, removeParentRelationshipInFlight] =
    useMutation<RemoveParentRelationshipMutation>(remove_parent_relationship);

  const removeParentRelationship = useCallback(
    (variables: { pupilId: string }) => {
      const removeParentRelationshipConfig = {
        variables: {
          connections:
            childrenConnections && route.params.accountType === "PARENT"
              ? [childrenConnections]
              : route.params.previousChilrenConnectionId
                ? [route.params.previousChilrenConnectionId]
                : [],
          input: variables,
        },
        onCompleted: (response: RemoveParentRelationshipMutation$data) => {
          if (response?.removeParentRelationship?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"Parent/child relationship removed"}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't remove parent/child relationship"}
                  toast={toast}
                />
              ),
            });
          }
          dispatchState({
            input: "clickedAccountId",
            value: undefined,
          });
        },
      };

      commitRemoveParentRelationship(removeParentRelationshipConfig);
    },
    [
      childrenConnections,
      commitRemoveParentRelationship,
      dispatchState,
      route.params.accountType,
      route.params.previousChilrenConnectionId,
      toast,
    ]
  );

  const [commitUpdateAccountStatus, updateAccountStatusInFlight] =
    useMutation<UpdateAccountStatusMutation>(update_account_status);

  const updateAccountStatus = useCallback(
    (variables: { status: string }) => {
      const connections = route.params.accountsHubConnectionIds ?? [];
      if (route.params.accountType === "PUPIL" && route.params.previousChilrenConnectionId) {
        connections.push(route.params.previousChilrenConnectionId);
      }
      const updateAccountStatusConfig = {
        variables: {
          connections: connections,
          input: { profileId: route.params.profileId, ...variables },
        },
        onCompleted: (response: UpdateAccountStatusMutation$data) => {
          if (response?.updateAccountStatus?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status={variables.status === "DELETE" ? "error" : "success"}
                  title={
                    variables.status === "ACTIVE"
                      ? "Made account active"
                      : variables.status === "ARCHIVED"
                        ? "Archived account"
                        : "Account deleted"
                  }
                  toast={toast}
                />
              ),
            });
            if (variables.status === "DELETE") {
              navigation.popToTop();
            }
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't update account status"}
                  toast={toast}
                />
              ),
            });
          }
        },
        updater: (store: RecordSourceSelectorProxy) => {
          // invalidate record in store so that queries for register action modals are refetched
          // (archived teachers are removed from those modals)
          if (route.params.accountType === "TEACHER") {
            const profileRecord = store.get(route.params.profileId);
            if (profileRecord) {
              profileRecord.invalidateRecord();
            }
          }
        },
      };

      commitUpdateAccountStatus(updateAccountStatusConfig);
    },
    [
      commitUpdateAccountStatus,
      navigation,
      route.params.accountType,
      route.params.accountsHubConnectionIds,
      route.params.previousChilrenConnectionId,
      route.params.profileId,
      toast,
    ]
  );

  const inputFinishEditingHandler = useCallback(
    (inputIdentifier?: string | number, inputValue?: string | number, isValid?: boolean) => {
      if (
        isValid &&
        inputIdentifier &&
        inputValue !== undefined &&
        // check if the value has changed before firing mutation
        inputValue !== accountData.profile?.[inputIdentifier as keyof typeof accountData.profile] &&
        inputValue !==
          accountData.profile?.user?.[inputIdentifier as keyof typeof accountData.profile.user]
      ) {
        switch (inputIdentifier) {
          case "discountPercentage":
            dispatchState({
              input: "discountPercentage",
              value: inputValue,
            });
            if (inputValue == 0) {
              dispatchState({
                input: "discountCategorySelect",
                value: "FULL_PRICE",
              });
            }
            editProfile({
              discountPercentage: inputValue as number,
              profileId: route.params.profileId,
            });
            return;
          default:
            editProfile({
              [inputIdentifier]: typeof inputValue === "string" ? inputValue.trim() : inputValue,
              profileId: route.params.profileId,
            });
        }
      }
    },
    [accountData, dispatchState, editProfile, route.params.profileId]
  );

  const discountPercentageInputRef = useRef<TextInputRef>(null);

  const selectChangeHandler = useCallback(
    (inputIdentifier: string, itemValue: string | boolean) => {
      if (state.values?.[`${inputIdentifier}Select` as keyof ReducerValues] != itemValue) {
        dispatchState({
          input: `${inputIdentifier}Select` as keyof ReducerValues,
          value: itemValue,
        });
        if (inputIdentifier === "discountCategory" && typeof itemValue === "string") {
          if (itemValue.includes("DISCOUNT")) {
            dispatchState({
              input: "discountPercentage",
              value: 100,
            });
            discountPercentageInputRef.current?.setValue("100%", true);
          } else {
            dispatchState({
              input: "discountPercentage",
              value: 0,
            });
            discountPercentageInputRef.current?.setValue("0%", true);
          }
        }

        let parsedItemValue: string | number | boolean = itemValue;
        if (inputIdentifier === "schoolYear") {
          parsedItemValue = Number(itemValue);
        }

        editProfile({
          [inputIdentifier]: parsedItemValue,
          profileId: route.params.profileId,
        });
      }
    },
    [state.values, dispatchState, editProfile, route.params.profileId]
  );

  const showUpdateBlockDetailsModalHandler = useCallback<ShowUpdateBlockDetailsModalHandler>(
    (updateBlockDetailsModalInfo) => {
      dispatchState({
        input: "updateBlockDetailsModalInfo",
        value: updateBlockDetailsModalInfo,
      });
    },
    [dispatchState]
  );

  const updateRegisterStateHandler = useCallback(
    (
      variables: {
        lessonId: string;
        registerNote?: string;
        status: string;
      },
      progressChange?: number
    ) => {
      const existingUpdateIndex = registerUpdates.current.findIndex(
        (item) => item.lessonId === variables.lessonId
      );
      if (existingUpdateIndex === -1) {
        registerUpdates.current.push(variables);
      } else {
        registerUpdates.current.splice(existingUpdateIndex, 1, variables);
      }
      // update the progress bar
      if (progressChange && registerProgressCounter.current.marked !== undefined) {
        registerProgressCounter.current.marked += progressChange;
      }
      // this is a key used to force a rerender when these refs change (refs do not cause re-render)
      dispatchState({ input: "registerUpdater", value: Math.random() });
    },
    [dispatchState]
  );

  const { isOpen: optionsIsOpen, onClose: optionsOnClose, onOpen: optionsOnOpen } = useDisclose();
  const {
    isOpen: changeStatusActionsheetIsOpen,
    onClose: changeStatusActionsheetOnClose,
    onOpen: changeStatusActionsheetOnOpen,
  } = useDisclose();

  const openLessonBlockOptionsHandler = useCallback(
    (lessonBlockId: string) => {
      dispatchState({
        input: "lessonBlockActionsheetId",
        value: lessonBlockId,
      });
      optionsOnOpen();
    },
    [dispatchState, optionsOnOpen]
  );

  const addressSecondLineInputRef = useRef<TextInputRef>(null);
  const cityInputRef = useRef<TextInputRef>(null);
  const postcodeInputRef = useRef<TextInputRef>(null);
  const dbsExpirationDateRef = useRef<TextInputRef>(null);

  const renderSideBar = useMemo(() => {
    return (
      <SideBar
        isCollapsed={state.values.sideBarIsCollapsed}
        navigation={navigation}
        setIsCollapsed={() =>
          dispatchState({
            input: "sideBarIsCollapsed",
            value: !state.values.sideBarIsCollapsed,
          })
        }
        width={state.values.sideBarIsCollapsed ? SIDE_BAR_COLLAPSED_WIDTH : SIDE_BAR_EXPANDED_WIDTH}
      >
        <VStack alignItems="center" mt="20" pb="6" space="4" width="100%">
          <VStack alignItems="center">
            <Avatar
              _text={{ color: "surface.200" }}
              bg="primary.600"
              size="md"
              source={{
                uri: undefined,
              }}
            >
              {getInitials(accountData.profile?.user.firstName, accountData.profile?.user.lastName)}
            </Avatar>
            <Text fontSize="xl" mt="2" textAlign="center">
              {getFullName(accountData.profile?.user.firstName, accountData.profile?.user.lastName)}
            </Text>
            <Text fontSize="lg">{titleCaseConverter(accountType)}</Text>
            {accountData.profile?.user.dateJoined ? (
              <Text color="surface.700" mt="2" textAlign="center">
                Joined {format(new Date(accountData.profile?.user.dateJoined), "d MMM yyyy")}
              </Text>
            ) : null}
          </VStack>
          {accountType !== "PUPIL" &&
          (accountData.profile?.accountNumber ||
            accountData.profile?.user.email ||
            accountData.profile?.phoneNumber) ? (
            <VStack alignItems="center">
              {accountData.profile?.accountNumber ? (
                <Text fontSize="md">Account #{accountData.profile.accountNumber}</Text>
              ) : null}
              {accountData.profile?.user.email ? (
                <Text fontSize="md">{accountData.profile.user.email}</Text>
              ) : null}
              {accountData.profile?.phoneNumber ? (
                <HStack space="1">
                  <Text fontSize="md">{accountData.profile.phoneNumber}</Text>
                  {accountData.profile?.hasInvalidPhoneNumber ? (
                    <WarningIcon color="yellow.500" size="5" />
                  ) : null}
                </HStack>
              ) : null}
            </VStack>
          ) : accountData.profile?.school?.name || accountData.profile?.instruments ? (
            <VStack alignItems="center">
              {accountData.profile?.school?.name ? (
                <Pressable
                  onPress={() => {
                    if (accountData.profile?.school?.id) {
                      navigation.push("School", {
                        schoolId: accountData.profile.school.id,
                        startingYear: state.values.startingYear,
                      });
                    }
                  }}
                >
                  <Text fontSize="md" textAlign="center">
                    {titleCaseConverter(accountData.profile.school.name)}
                  </Text>
                </Pressable>
              ) : null}
              {accountData.profile?.instruments || accountData.profile?.schoolYear ? (
                <Text fontSize="md">
                  {accountData.profile?.schoolYear != null
                    ? schoolYearConverter(accountData.profile.schoolYear) +
                      (accountData.profile?.instruments != null &&
                      accountData.profile?.instruments.length > 0
                        ? " • "
                        : "")
                    : ""}
                  {accountData.profile?.instruments != null
                    ? titleCaseConverter(
                        accountData.profile?.instruments.toString().replace(",", ", ")
                      )
                    : ""}
                </Text>
              ) : null}
            </VStack>
          ) : null}
          <Pressable alignItems="center" onPress={() => changeStatusActionsheetOnOpen()}>
            <ItemStatus
              isLoading={updateAccountStatusInFlight}
              mt="2"
              showEditArrow
              status={accountData.profile?.status}
            />
          </Pressable>
          {accountType === "TEACHER" ? (
            <DbsStatus
              dbsExpirationDate={accountData.profile?.dbsExpirationDate}
              dbsNumber={accountData.profile?.dbsNumber}
              showLongText
            />
          ) : null}
          <VStack alignItems="center" space="2" width="100%">
            {accountType === "PARENT" ? (
              <VStack alignItems="center" mb="2" space="1">
                {(accountData.profile?.children.edges?.length ?? 0) > 0 ? (
                  <Text color="surface.700">{`${
                    (accountData.profile?.children.edges?.length ?? 2) > 1 ? "Children" : "Child"
                  }`}</Text>
                ) : null}
                {accountData.profile?.children.edges?.map((item) => {
                  if (item?.node) {
                    return (
                      <HStack key={item.node.id}>
                        <Pressable
                          _hover={{
                            bg: "surface.400",
                          }}
                          _pressed={{
                            bg: "surface.500",
                          }}
                          borderRadius="2xl"
                          onPress={() => {
                            if (item.node?.id) {
                              navigation.push("Account", {
                                accountType: "PUPIL",
                                block: state.values.block,
                                previousChilrenConnectionId: childrenConnections,
                                profileId: item.node.id,
                                startingYear: state.values.startingYear,
                              });
                            }
                          }}
                          px="2"
                        >
                          <Text
                            fontSize="md"
                            fontWeight="600"
                            textAlign="center"
                            textDecorationLine="underline"
                          >
                            {getFullName(item?.node?.user.firstName, item?.node?.user.lastName)}
                          </Text>
                        </Pressable>
                        <Button
                          _hover={{
                            bg: "transparent",
                            opacity: 0.8,
                          }}
                          _pressed={{
                            bg: "transparent",
                            opacity: 0.7,
                          }}
                          _spinner={{
                            color: "surface.700",
                          }}
                          isLoading={
                            removeParentRelationshipInFlight &&
                            state.values.clickedAccountId === item.node.id
                          }
                          leftIcon={<CloseIcon color="surface.700" size="4" />}
                          onPress={() => {
                            if (item.node?.id) {
                              dispatchState({
                                input: "clickedAccountId",
                                value: item.node.id,
                              });
                              dispatchState({
                                input: "removeParentRelationshipAlertIsOpen",
                                value: true,
                              });
                            }
                          }}
                          p="1"
                          position="absolute"
                          right="-22"
                          variant="ghost"
                        />
                      </HStack>
                    );
                  } else {
                    return null;
                  }
                })}
                <Button
                  bg="primary.400"
                  leftIcon={<ProfileIcon size="5" />}
                  mt="2"
                  onPress={() => {
                    dispatchState({
                      input: "createAccountModalIsOpen",
                      value: true,
                    });
                  }}
                  shadow={1}
                >
                  Add Child
                </Button>
                <VStack alignItems="center" mt="2" space="1">
                  {(accountData.profile?.parentEnrolments.edges?.length ?? 0) > 0 ? (
                    <Text color="surface.700">{`Enrolment${
                      accountData.profile?.parentEnrolments.edges?.length === 1 ? "" : "s"
                    }`}</Text>
                  ) : null}
                  {accountData.profile?.parentEnrolments.edges?.map((item) => {
                    if (!item?.node) {
                      return null;
                    }

                    return (
                      <Pressable
                        key={item.node.id}
                        _hover={{
                          bg: "surface.400",
                        }}
                        _pressed={{
                          bg: "surface.500",
                        }}
                        borderRadius="2xl"
                        onPress={() => {
                          if (item.node?.id) {
                            navigation.push("Enrolment", {
                              enrolmentId: item.node.id,
                            });
                          }
                        }}
                        px="2"
                      >
                        <Text
                          fontSize="md"
                          fontWeight="600"
                          textAlign="center"
                          textDecorationLine="underline"
                        >
                          {`${getFullName(item.node.pupilFirstName, item.node.pupilLastName)}${item.node.enroledTimestamp ? ` (${format(ensureDateType(item.node.enroledTimestamp), "dd/MM/yyyy")})` : ""}`}
                        </Text>
                      </Pressable>
                    );
                  })}
                </VStack>
              </VStack>
            ) : accountType === "PUPIL" ? (
              <VStack alignItems="center" mb="2" space="3">
                <VStack alignItems="center" space="1">
                  <Text color="surface.700">Parent</Text>
                  {accountData.profile?.primaryParent ? (
                    <HStack>
                      <Pressable
                        _hover={{ bg: "surface.400" }}
                        _pressed={{ bg: "surface.500" }}
                        borderRadius="2xl"
                        onPress={() => {
                          if (accountData.profile?.primaryParent?.id) {
                            navigation.push("Account", {
                              accountType: "PARENT",
                              block: state.values.block,
                              profileId: accountData.profile.primaryParent.id,
                              startingYear: state.values.startingYear,
                            });
                          }
                        }}
                        px="2"
                      >
                        <Text fontSize="md" fontWeight="600" textDecorationLine="underline">
                          {getFullName(
                            accountData.profile.primaryParent.user.firstName,
                            accountData.profile.primaryParent.user.lastName
                          )}
                        </Text>
                      </Pressable>
                      <Button
                        _hover={{
                          bg: "transparent",
                          opacity: 0.8,
                        }}
                        _pressed={{
                          bg: "transparent",
                          opacity: 0.7,
                        }}
                        _spinner={{
                          color: "surface.700",
                        }}
                        isLoading={
                          removeParentRelationshipInFlight &&
                          state.values.clickedAccountId === route.params.profileId
                        }
                        leftIcon={<CloseIcon color="surface.700" size="4" />}
                        onPress={() => {
                          dispatchState({
                            input: "clickedAccountId",
                            value: route.params.profileId,
                          });
                          dispatchState({
                            input: "removeParentRelationshipAlertIsOpen",
                            value: true,
                          });
                        }}
                        p="1"
                        position="absolute"
                        right="-22"
                        variant="ghost"
                      />
                    </HStack>
                  ) : (
                    <Button
                      bg="primary.400"
                      leftIcon={<ProfileIcon size="5" />}
                      mt="2"
                      onPress={() => {
                        dispatchState({
                          input: "createAccountModalIsOpen",
                          value: true,
                        });
                      }}
                      shadow={1}
                    >
                      Add Parent
                    </Button>
                  )}
                </VStack>
                {accountData.profile?.teachers?.length ? (
                  <VStack alignItems="center" space="1">
                    <Text color="surface.700">
                      Teacher
                      {accountData.profile?.teachers?.length === 1 ? "" : "s"}
                    </Text>
                    {accountData.profile?.teachers?.map((item, index) => {
                      return (
                        <Pressable
                          key={index}
                          _hover={{
                            bg: "surface.400",
                          }}
                          _pressed={{
                            bg: "surface.500",
                          }}
                          borderRadius="2xl"
                          onPress={() => {
                            if (item?.profileId) {
                              navigation.push("Account", {
                                accountType: "TEACHER",
                                block: state.values.block,
                                profileId: item.profileId,
                                startingYear: state.values.startingYear,
                              });
                            }
                          }}
                          px="2"
                        >
                          <Text fontSize="md" fontWeight="600" textDecorationLine="underline">
                            {getFullName(
                              item?.profileFirstName ?? undefined,
                              item?.profileLastName ?? undefined
                            )}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </VStack>
                ) : null}
                <VStack alignItems="center" mt="2" space="1">
                  {(accountData.profile?.pupilEnrolments.edges?.length ?? 0) > 0 ? (
                    <Text color="surface.700">{`Enrolment${
                      accountData.profile?.pupilEnrolments.edges?.length === 1 ? "" : "s"
                    }`}</Text>
                  ) : null}
                  {accountData.profile?.pupilEnrolments.edges?.map((item) => {
                    if (!item?.node) {
                      return null;
                    }

                    return (
                      <Pressable
                        key={item.node.id}
                        _hover={{
                          bg: "surface.400",
                        }}
                        _pressed={{
                          bg: "surface.500",
                        }}
                        borderRadius="2xl"
                        onPress={() => {
                          if (item.node?.id) {
                            navigation.push("Enrolment", {
                              enrolmentId: item.node.id,
                            });
                          }
                        }}
                        px="2"
                      >
                        <Text
                          fontSize="md"
                          fontWeight="600"
                          textAlign="center"
                          textDecorationLine="underline"
                        >
                          {`${item.node.enroledTimestamp || accountData.profile?.user.dateJoined ? `${format(ensureDateType(item.node.enroledTimestamp ?? accountData.profile?.user.dateJoined), "dd/MM/yyyy")}` : "Undated Enrolment"}`}
                        </Text>
                      </Pressable>
                    );
                  })}
                </VStack>
              </VStack>
            ) : null}
            <TextInput
              autoCapitalize="words"
              id="firstName"
              initiallyValid={Boolean(accountData.profile?.user.firstName)}
              initialValue={accountData.profile?.user.firstName ?? ""}
              invalidIndicator
              label="First name"
              labelStyle={{
                color: "surface.700",
                fontSize: "sm",
              }}
              onFinishEditing={inputFinishEditingHandler}
              size="md"
            />
            <TextInput
              autoCapitalize="words"
              id="lastName"
              initiallyValid
              initialValue={accountData.profile?.user.lastName ?? ""}
              invalidIndicator
              label="Last name"
              labelStyle={{
                color: "surface.700",
                fontSize: "sm",
              }}
              onFinishEditing={inputFinishEditingHandler}
              size="md"
            />
            {accountType !== "PUPIL" ? (
              <>
                <TextInput
                  autoCapitalize="none"
                  email
                  id="email"
                  initiallyValid
                  initialValue={accountData.profile?.user.email ?? ""}
                  invalidIndicator
                  keyboardType="email-address"
                  label="Email"
                  labelStyle={{
                    color: "surface.700",
                    fontSize: "sm",
                  }}
                  onFinishEditing={inputFinishEditingHandler}
                  size="md"
                />
                <TextInput
                  ref={phoneNumberInputRef}
                  autoCapitalize="none"
                  id="phoneNumber"
                  initiallyValid={!accountData.profile?.hasInvalidPhoneNumber}
                  initialValue={accountData.profile?.phoneNumber ?? ""}
                  invalidIndicator
                  isInvalid={accountData.profile?.hasInvalidPhoneNumber}
                  keyboardType="phone-pad"
                  label="Phone number"
                  labelStyle={{
                    color: "surface.700",
                    fontSize: "sm",
                  }}
                  onFinishEditing={inputFinishEditingHandler}
                  size="md"
                />
              </>
            ) : (
              <>
                <VStack mt="1" space="0.5" width="100%">
                  <Text color="surface.700" fontSize="sm" pl="1">
                    School
                  </Text>
                  <Select
                    bg="surface.100"
                    borderRadius="full"
                    color="surface.900"
                    fontSize="sm"
                    onValueChange={(itemValue) => selectChangeHandler("schoolId", itemValue)}
                    placeholder="Select school"
                    selectedValue={state.values.schoolIdSelect}
                  >
                    {allSchools.map((item) => {
                      if (!item?.node?.id) {
                        return null;
                      }

                      const isArchived = item.node.archived;

                      return (
                        <Select.Item
                          key={item.node.id}
                          actionSheetLabel={`${titleCaseConverter(item.node.name)} ${
                            isArchived ? "(Archived)" : ""
                          }`}
                          value={item.node.id}
                        />
                      );
                    })}
                  </Select>
                </VStack>
                <VStack mt="1" space="0.5" width="100%">
                  <Text color="surface.700" fontSize="sm" pl="1">
                    School year
                  </Text>
                  <Select
                    bg="surface.100"
                    borderRadius="full"
                    color="surface.900"
                    fontSize="sm"
                    onValueChange={(itemValue) => selectChangeHandler("schoolYear", itemValue)}
                    placeholder="Select school year"
                    selectedValue={state.values.schoolYearSelect}
                  >
                    {SCHOOL_YEARS.map((item) => {
                      return (
                        <Select.Item
                          key={item.value}
                          actionSheetLabel={item.label}
                          value={item.value}
                        />
                      );
                    })}
                  </Select>
                </VStack>
                <TextInput
                  autoCapitalize="words"
                  id="schoolClass"
                  initiallyValid
                  initialValue={accountData.profile?.schoolClass ?? ""}
                  label="Class"
                  labelStyle={{
                    color: "surface.700",
                    fontSize: "sm",
                  }}
                  maxLength={200}
                  onFinishEditing={inputFinishEditingHandler}
                  size="md"
                />
                <VStack mt="1" space="0.5" width="100%">
                  <Text color="surface.700" fontSize="sm" pl="1">
                    Lessons paid by school?
                  </Text>
                  <Select
                    _disabledBg="surface.100"
                    _disabledBorderWidth={1}
                    _disabledOpacity={0.9}
                    bg="surface.100"
                    borderRadius="full"
                    color="surface.900"
                    fontSize="sm"
                    onValueChange={(itemValue) =>
                      selectChangeHandler("paidBySchool", itemValue === "YES")
                    }
                    placeholder="Lessons paid by school?"
                    selectedValue={state.values.paidBySchoolSelect ? "YES" : "NO"}
                  >
                    <Select.Item key={"YES"} actionSheetLabel={"Yes"} value={"YES"} />
                    <Select.Item key={"NO"} actionSheetLabel={"No"} value={"NO"} />
                  </Select>
                </VStack>
                <VStack mt="1" space="0.5" width="100%">
                  <Text color="surface.700" fontSize="sm" pl="1">
                    Fee type
                  </Text>
                  <Select
                    bg="surface.100"
                    borderRadius="full"
                    color="surface.900"
                    fontSize="sm"
                    onValueChange={(itemValue) =>
                      selectChangeHandler("discountCategory", itemValue)
                    }
                    placeholder="Select fee type"
                    selectedValue={state.values.discountCategorySelect}
                  >
                    {DISCOUNT_CATEGORIES.map((item) => {
                      return (
                        <Select.Item
                          key={item.value}
                          actionSheetLabel={item.label}
                          value={item.value}
                        />
                      );
                    })}
                  </Select>
                </VStack>
                {state.values.discountCategorySelect?.includes("DISCOUNT") ? (
                  <PercentageInput
                    ref={discountPercentageInputRef}
                    autoCapitalize="none"
                    id="discountPercentage"
                    initiallyValid={isNumeric(accountData.profile?.discountPercentage)}
                    initialValue={state.values.discountPercentage}
                    invalidIndicator
                    label="Discount percentage"
                    labelStyle={{
                      color: "surface.700",
                      fontSize: "sm",
                    }}
                    onFinishEditing={inputFinishEditingHandler}
                    size="md"
                  />
                ) : null}
                <HStack mt="3" space="3" width="100%">
                  <Text color="surface.700" fontSize="sm" pl="1">
                    Has book?
                  </Text>
                  <Checkbox
                    defaultIsChecked={accountData.profile?.hasBook ?? false}
                    onChange={(isSelected) => selectChangeHandler("hasBook", isSelected)}
                    size="md"
                    value="HAS_BOOK"
                  />
                </HStack>
                <HStack space="3" width="100%">
                  <Text color="surface.700" fontSize="sm" pl="1">
                    Has instrument?
                  </Text>
                  <Checkbox
                    defaultIsChecked={accountData.profile?.hasInstrument ?? false}
                    onChange={(isSelected) => selectChangeHandler("hasInstrument", isSelected)}
                    size="md"
                    value="HAS_INSTRUMENT"
                  />
                </HStack>
                <HStack space="3" width="100%">
                  <Text color="surface.700" fontSize="sm" pl="1">
                    Instrument supplied by us?
                  </Text>
                  <Checkbox
                    defaultIsChecked={accountData.profile?.instrumentSuppliedByUs ?? false}
                    onChange={(isSelected) =>
                      selectChangeHandler("instrumentSuppliedByUs", isSelected)
                    }
                    size="md"
                    value="INSTRUMENT_SUPPLIED_BY_US"
                  />
                </HStack>
                <HStack mb="2" space="3" width="100%">
                  <Text color="surface.700" fontSize="sm" pl="1">
                    Has returned instrument?
                  </Text>
                  <Checkbox
                    defaultIsChecked={accountData.profile?.hasReturnedInstrument ?? false}
                    onChange={(isSelected) =>
                      selectChangeHandler("hasReturnedInstrument", isSelected)
                    }
                    size="md"
                    value="HAS_RETURNED_INSTRUMENT"
                  />
                </HStack>
                <TextInput
                  autoCapitalize="sentences"
                  id="staffNoteForTeacher"
                  initiallyValid
                  initialValue={accountData.profile?.staffNoteForTeacher ?? ""}
                  label="Notes"
                  labelStyle={{
                    color: "surface.700",
                    fontSize: "sm",
                  }}
                  maxLength={500}
                  onFinishEditing={inputFinishEditingHandler}
                  size="md"
                />
              </>
            )}
            {accountType === "TEACHER" ? (
              <>
                <TextInput
                  autoCapitalize="none"
                  id="addressFirstLine"
                  initiallyValid
                  initialValue={accountData.profile?.addressFirstLine ?? ""}
                  invalidIndicator
                  label="Address"
                  labelStyle={{
                    color: "surface.700",
                    fontSize: "sm",
                  }}
                  maxLength={200}
                  onFinishEditing={inputFinishEditingHandler}
                  onSubmit={() => addressSecondLineInputRef.current?.focus()}
                  placeholder="1st line address"
                  size="md"
                />
                <TextInput
                  ref={addressSecondLineInputRef}
                  autoCapitalize="none"
                  id="addressSecondLine"
                  initiallyValid
                  initialValue={accountData.profile?.addressSecondLine ?? ""}
                  invalidIndicator
                  maxLength={200}
                  onFinishEditing={inputFinishEditingHandler}
                  onSubmit={() => cityInputRef.current?.focus()}
                  placeholder="2nd line address"
                  size="md"
                />
                <TextInput
                  ref={cityInputRef}
                  autoCapitalize="none"
                  id="city"
                  initiallyValid
                  initialValue={accountData.profile?.city ?? ""}
                  invalidIndicator
                  maxLength={50}
                  onFinishEditing={inputFinishEditingHandler}
                  onSubmit={() => postcodeInputRef.current?.focus()}
                  placeholder="City or county"
                  size="md"
                />
                <TextInput
                  ref={postcodeInputRef}
                  autoCapitalize="none"
                  id="postcode"
                  initiallyValid
                  initialValue={accountData.profile?.postcode ?? ""}
                  invalidIndicator
                  maxLength={10}
                  onFinishEditing={inputFinishEditingHandler}
                  placeholder="Postcode"
                  size="md"
                />
                <TextInput
                  autoCapitalize="none"
                  id="dbsNumber"
                  initiallyValid
                  initialValue={accountData.profile?.dbsNumber ?? ""}
                  invalidIndicator
                  label="DBS number"
                  labelStyle={{
                    color: "surface.700",
                    fontSize: "sm",
                  }}
                  maxLength={50}
                  onFinishEditing={inputFinishEditingHandler}
                  size="md"
                />
                <TextInput
                  ref={dbsExpirationDateRef}
                  id="dbsExpirationDate"
                  initiallyValid
                  initialValue={
                    accountData.profile?.dbsExpirationDate
                      ? format(new Date(accountData.profile.dbsExpirationDate), "dd/MM/yyyy")
                      : ""
                  }
                  label="DBS expiration date"
                  labelStyle={{
                    color: "surface.700",
                    fontSize: "sm",
                  }}
                  onFocus={() =>
                    dispatchState({
                      input: "dbsExpiryDatePickerIsOpen",
                      value: true,
                    })
                  }
                  size="md"
                />
              </>
            ) : null}
          </VStack>
        </VStack>
      </SideBar>
    );
  }, [
    state.values.sideBarIsCollapsed,
    state.values.clickedAccountId,
    state.values.schoolIdSelect,
    state.values.schoolYearSelect,
    state.values.paidBySchoolSelect,
    state.values.discountCategorySelect,
    state.values.discountPercentage,
    state.values.startingYear,
    state.values.block,
    navigation,
    accountData.profile?.user.firstName,
    accountData.profile?.user.lastName,
    accountData.profile?.user.dateJoined,
    accountData.profile?.user.email,
    accountData.profile?.accountNumber,
    accountData.profile?.phoneNumber,
    accountData.profile?.hasInvalidPhoneNumber,
    accountData.profile?.school?.name,
    accountData.profile?.school?.id,
    accountData.profile?.instruments,
    accountData.profile?.schoolYear,
    accountData.profile?.status,
    accountData.profile?.dbsExpirationDate,
    accountData.profile?.dbsNumber,
    accountData.profile?.children.edges,
    accountData.profile?.primaryParent,
    accountData.profile?.teachers,
    accountData.profile?.schoolClass,
    accountData.profile?.discountPercentage,
    accountData.profile?.hasBook,
    accountData.profile?.hasInstrument,
    accountData.profile?.instrumentSuppliedByUs,
    accountData.profile?.hasReturnedInstrument,
    accountData.profile?.staffNoteForTeacher,
    accountData.profile?.addressFirstLine,
    accountData.profile?.addressSecondLine,
    accountData.profile?.city,
    accountData.profile?.postcode,
    accountData.profile?.parentEnrolments.edges,
    accountData.profile?.pupilEnrolments.edges,
    accountType,
    updateAccountStatusInFlight,
    removeParentRelationshipInFlight,
    route.params.profileId,
    inputFinishEditingHandler,
    allSchools,
    dispatchState,
    changeStatusActionsheetOnOpen,
    childrenConnections,
    selectChangeHandler,
  ]);

  const renderTab = useMemo(() => {
    switch (accountType) {
      case "TEACHER":
        return (
          <>
            {state.values.currentPageIndex == 0 ? (
              <TeacherRegisters
                allSchools={allSchools}
                dispatchState={dispatchState}
                lessonBlocksConnectionId={state.values.lessonBlocksConnectionId}
                loadLessonBlocksQuery={loadLessonBlocksQuery}
                loadLessonBlocksQueryReference={loadLessonBlocksQueryReference}
                navigation={navigation}
                profileFirstName={accountData.profile?.user.firstName}
                profileId={route.params.profileId}
                profileLastName={accountData.profile?.user.lastName}
                registerProgressCounter={registerProgressCounter}
                registerUpdates={registerUpdates}
                removeLessonBlocks={removeLessonBlocks}
                removeLessonBlocksInFlight={removeLessonBlocksInFlight}
                showUpdateBlockDetailsModalHandler={showUpdateBlockDetailsModalHandler}
                sideBarWidth={
                  state.values.sideBarIsCollapsed
                    ? SIDE_BAR_COLLAPSED_WIDTH
                    : SIDE_BAR_EXPANDED_WIDTH
                }
                startingYears={startingYears}
                state={state}
                teacherSchools={teacherSchools}
                updateLessonBlockDetails={updateLessonBlockDetails}
                updateRegisterState={updateRegisterStateHandler}
                updateScheduledLessonBlockTime={updateScheduledLessonBlockTime}
                updateScheduledLessonBlockTimeInFlight={updateScheduledLessonBlockTimeInFlight}
                userIsTeacher={false}
              />
            ) : state.values.currentPageIndex == 1 ? (
              <TeacherTimetable
                dispatchState={dispatchState}
                loadLessonsQuery={loadLessonsQuery}
                loadLessonsQueryReference={loadLessonsQueryReference}
                navigation={navigation}
                profileId={route.params.profileId}
                state={state}
                userIsTeacher={false}
              />
            ) : (
              <ListEmptyBanner
                explainer={"This hasn't been built yet..."}
                flexGrow={1}
                fontSize="3xl"
                py="4"
              >
                <Text fontSize="110">{"👷"}</Text>
              </ListEmptyBanner>
            )}
          </>
        );
      case "PUPIL":
        return (
          <>
            {state.values.currentPageIndex == 0 ? (
              loadLessonBlocksQueryReference != null ? (
                <Suspense fallback={<LoadingBlobs>Loading Registers...</LoadingBlobs>}>
                  <PupilRegisters
                    dispatchState={dispatchState}
                    loadLessonBlocksQuery={loadLessonBlocksQuery}
                    loadLessonBlocksQueryReference={loadLessonBlocksQueryReference}
                    navigation={navigation}
                    openLessonBlockOptionsHandler={openLessonBlockOptionsHandler}
                    profileId={route.params.profileId}
                    registerUpdates={registerUpdates}
                    showUpdateBlockDetailsModalHandler={showUpdateBlockDetailsModalHandler}
                    sideBarWidth={
                      state.values.sideBarIsCollapsed
                        ? SIDE_BAR_COLLAPSED_WIDTH
                        : SIDE_BAR_EXPANDED_WIDTH
                    }
                    startingYears={startingYears}
                    state={state}
                    updateRegisterState={updateRegisterStateHandler}
                  />
                </Suspense>
              ) : (
                <LoadingBlobs>Loading Registers...</LoadingBlobs>
              )
            ) : (
              <ListEmptyBanner
                explainer={"This hasn't been built yet..."}
                flexGrow={1}
                fontSize="3xl"
                py="4"
              >
                <Text fontSize="110">{"👷"}</Text>
              </ListEmptyBanner>
            )}
          </>
        );
      case "PARENT":
        return (
          <>
            {state.values.currentPageIndex == 0 ? (
              <InvoiceAndEmail
                accountScreenState={state}
                dispatchAccountScreenState={dispatchState}
                firstName={accountData.profile?.user.firstName}
                isAccountScreen
                isEditable
                lastName={accountData.profile?.user.lastName}
                loadInvoiceQuery={loadInvoiceQuery}
                loadInvoiceQueryReference={loadInvoiceQueryReference}
                // @ts-expect-error nav props are merged
                navigation={navigation}
                previousOtherInvoicesConnectionIds={route.params.previousOtherInvoicesConnectionIds}
                profileId={route.params.profileId}
                route={route}
                sideBarWidth={
                  state.values.sideBarIsCollapsed
                    ? SIDE_BAR_COLLAPSED_WIDTH
                    : SIDE_BAR_EXPANDED_WIDTH
                }
                startingYears={startingYears}
              />
            ) : (
              <ListEmptyBanner
                explainer={"This hasn't been built yet..."}
                flexGrow={1}
                fontSize="3xl"
                py="4"
              >
                <Text fontSize="110">{"👷"}</Text>
              </ListEmptyBanner>
            )}
          </>
        );
      default:
        return (
          <ListEmptyBanner
            explainer={"This hasn't been built yet..."}
            flex={1}
            flexGrow={1}
            fontSize="3xl"
            py="4"
          >
            <Text fontSize="110">{"👷"}</Text>
          </ListEmptyBanner>
        );
    }
  }, [
    accountType,
    state,
    dispatchState,
    startingYears,
    teacherSchools,
    allSchools,
    route,
    accountData.profile?.user.firstName,
    accountData.profile?.user.lastName,
    loadLessonBlocksQuery,
    loadLessonBlocksQueryReference,
    updateLessonBlockDetails,
    updateScheduledLessonBlockTime,
    updateScheduledLessonBlockTimeInFlight,
    updateRegisterStateHandler,
    showUpdateBlockDetailsModalHandler,
    removeLessonBlocks,
    removeLessonBlocksInFlight,
    navigation,
    loadLessonsQuery,
    loadLessonsQueryReference,
    openLessonBlockOptionsHandler,
    loadInvoiceQuery,
    loadInvoiceQueryReference,
  ]);

  const renderModals = useMemo(() => {
    return (
      <>
        {state.values.createAccountModalIsOpen ? (
          <CreateAccountModal
            initialAccountType={accountType === "PARENT" ? "PUPIL" : "PARENT"}
            isAccountPage
            profileId={route.params.profileId}
            pupilConnections={
              accountType === "PARENT" && childrenConnections ? [childrenConnections] : undefined
            }
            schools={allSchools}
            setShowModal={(isOpen) => {
              dispatchState({
                input: "createAccountModalIsOpen",
                value: isOpen,
              });
            }}
            showModal={state.values.createAccountModalIsOpen}
          />
        ) : null}
        {accountType === "PUPIL" || accountType === "TEACHER" ? (
          <PupilsToArchiveModal
            block={state.values.block}
            hideModal={() =>
              dispatchState({
                input: "pupilsToArchiveModalInfo",
                value: undefined,
              })
            }
            pupilIds={state.values.pupilsToArchiveModalInfo?.ids}
            pupilNames={state.values.pupilsToArchiveModalInfo?.pupilNames}
            showModal={Boolean(state.values.pupilsToArchiveModalInfo)}
            startingYear={state.values.startingYear}
          />
        ) : null}
        <DateTimePickerModal
          accentColor={customColors.primary[800]}
          animationType="fade"
          canChooseEndTime
          date={state.values.dateTimePickerModalDate}
          duration={state.values.dateTimePickerModalLessonDuration}
          hours={state.values.dateTimePickerModalDate?.getHours()}
          label="Reschedule Lesson"
          locale="en"
          minutes={state.values.dateTimePickerModalDate?.getMinutes()}
          onConfirm={(params) => {
            if (state.values.dateTimePickerModalLessonId && params.date) {
              rescheduleLesson(
                state.values.dateTimePickerModalLessonId,
                params.date,
                params.duration
              );
            }
            dispatchState({
              input: "dateTimePickerModalDate",
              value: undefined,
            });
            dispatchState({
              input: "dateTimePickerModalLessonId",
              value: undefined,
            });
          }}
          onDismiss={() =>
            dispatchState({
              input: "dateTimePickerModalDate",
              value: undefined,
            })
          }
          saveLabel="Save"
          startWeekOnMonday
          textStyle={{
            fontFamily: "Poppins-SemiBold",
          }}
          uppercase={false}
          visible={state.values.dateTimePickerModalDate !== undefined}
        />
        <DayTimePickerModal
          accentColor={customColors.primary[800]}
          animationType="fade"
          dayIndex={(state.values.dayIndex ?? 0) + 1}
          hideDayPicker
          hours={9}
          isLoading={addNewLessonBlockInFlight}
          label="Add Lesson Block"
          locale="en"
          minutes={0}
          onConfirm={(params) => {
            if (!state.values.isAddLessonBreakModal) {
              if (state.values.selectedSchoolIdOnModal) {
                addNewLessonBlock({
                  block: state.values.block,
                  dayIndex: params.dayIndex,
                  hours: params.hours,
                  minutes: params.minutes,
                  schoolId: state.values.selectedSchoolIdOnModal,
                  startingYear: state.values.startingYear,
                  type: "LESSON",
                });
              }
            } else {
              if (state.values.selectedSchoolIdOnModal) {
                dispatchState({
                  input: "addLessonBlockModalIsOpen",
                  value: false,
                });
                dispatchState({
                  input: "schoolId",
                  value: state.values.selectedSchoolIdOnModal,
                });
                dispatchState({
                  input: "dayIndex",
                  value: params.dayIndex - 1,
                });
                dispatchState({
                  input: "hours",
                  value: params.hours,
                });
                dispatchState({
                  input: "minutes",
                  value: params.minutes,
                });
                dispatchState({
                  input: "addBreakBlockModalIsOpen",
                  value: true,
                });
              }
            }
          }}
          onDismiss={() =>
            dispatchState({
              input: "addLessonBlockModalIsOpen",
              value: false,
            })
          }
          saveLabel="Add"
          textStyle={{
            fontFamily: "Poppins-SemiBold",
          }}
          uppercase={false}
          visible={Boolean(state.values.addLessonBlockModalIsOpen)}
        />
        <DatePickerModal
          accentColor={customColors.primary[800]}
          animationType="fade"
          date={ensureDateType(accountData.profile?.dbsExpirationDate)}
          label="Select Date"
          locale="en"
          mode="single"
          onConfirm={(params) => {
            if (params.date) {
              dbsExpirationDateRef.current?.setValue(format(params.date, "dd/MM/yyyy"), true);
              editProfile({
                dbsExpirationDate: params.date.toDateString(),
                profileId: route.params.profileId,
              });
            }
            dispatchState({
              input: "dbsExpiryDatePickerIsOpen",
              value: false,
            });
          }}
          onDismiss={() =>
            dispatchState({
              input: "dbsExpiryDatePickerIsOpen",
              value: false,
            })
          }
          saveLabel="Select"
          startWeekOnMonday
          textStyle={{
            fontFamily: "Poppins-SemiBold",
          }}
          uppercase={false}
          visible={Boolean(state.values.dbsExpiryDatePickerIsOpen)}
        />
        <UpdateLessonBlockDetailsModal
          block={state.values.block}
          detailType={state.values.updateBlockDetailsModalInfo?.type}
          hideModal={() => {
            dispatchState({
              input: "updateBlockDetailsModalInfo",
              value: undefined,
            });
          }}
          lessonBlockCursor={state.values.updateBlockDetailsModalInfo?.lessonBlockCursor}
          lessonBlockIds={state.values.updateBlockDetailsModalInfo?.lessonBlockIds}
          onUpdate={state.values.updateBlockDetailsModalInfo?.onUpdate}
          schoolId={state.values.updateBlockDetailsModalInfo?.schoolId}
          showModal={
            Boolean(state.values.updateBlockDetailsModalInfo?.lessonBlockIds) &&
            Boolean(state.values.updateBlockDetailsModalInfo?.lessonBlockIds?.length) &&
            (state.values.updateBlockDetailsModalInfo?.type === "INSTRUMENT" ||
              state.values.updateBlockDetailsModalInfo?.type === "STAGE" ||
              state.values.updateBlockDetailsModalInfo?.type === "NOTES")
          }
          startingYear={state.values.startingYear}
          updateLessonBlockDetails={updateLessonBlockDetails}
          updateLessonBlockDetailsInFlight={updateLessonBlockDetailsInFlight}
        />
        <UpdateLessonBlockTeacherOrPupilModal
          dayIndex={state.values.dayIndex}
          detailType={
            state.values.updateBlockDetailsModalInfo?.type as
              | "PUPIL"
              | "TEACHER"
              | "TEACHER_AND_DAY"
              | undefined
          }
          hideModal={() => {
            dispatchState({
              input: "updateBlockDetailsModalInfo",
              // preserve this info to avoid unnecessary re-fetching
              value: {
                schoolId: state.values.updateBlockDetailsModalInfo?.schoolId,
                type: state.values.updateBlockDetailsModalInfo?.type,
              },
            });
          }}
          keepFree={state.values.updateBlockDetailsModalInfo?.keepFree}
          lessonBlockDataForPupilChange={
            state.values.updateBlockDetailsModalInfo?.lessonBlockDataForPupilChange
          }
          lessonBlockIds={state.values.updateBlockDetailsModalInfo?.lessonBlockIds}
          onUpdate={state.values.updateBlockDetailsModalInfo?.onUpdate}
          schoolId={state.values.updateBlockDetailsModalInfo?.schoolId}
          selectedBlock={state.values.block}
          selectedProfileInfo={state.values.updateBlockDetailsModalInfo?.selectedProfileInfo}
          selectedStartingYear={state.values.startingYear}
          showModal={
            Boolean(state.values.updateBlockDetailsModalInfo?.lessonBlockIds) &&
            (state.values.updateBlockDetailsModalInfo?.type === "TEACHER" ||
              state.values.updateBlockDetailsModalInfo?.type === "PUPIL" ||
              state.values.updateBlockDetailsModalInfo?.type === "TEACHER_AND_DAY")
          }
          updateLessonBlockDetails={updateLessonBlockDetails}
          updateLessonBlockDetailsInFlight={updateLessonBlockDetailsInFlight}
          updateScheduledLessonBlockTime={updateScheduledLessonBlockTime}
          updateScheduledLessonBlockTimeInFlight={updateScheduledLessonBlockTimeInFlight}
        />
        <Modal
          isOpen={state.values.selectSchoolModalIsOpen}
          onClose={() => {
            dispatchState({
              input: "selectSchoolModalIsOpen",
              value: false,
            });
          }}
          size="xl"
        >
          <Modal.Content bg="primary.800">
            <Modal.CloseButton
              _hover={{ bg: "transparent", opacity: 0.7 }}
              _icon={{ color: "surface.100" }}
              _pressed={{ bg: "transparent", opacity: 0.7 }}
            />
            <Modal.Header _text={{ color: "surface.100", fontSize: "2xl" }} bg="primary.800">
              {state.values.isAddLessonBreakModal ? "Add Lesson Break" : "Add Lesson Block"}
            </Modal.Header>
            <Modal.Body alignItems="center" mb="4" mt="8" px="6">
              <VStack alignItems="center" space="8">
                <VStack alignItems="center" flex={1}>
                  <Text color="surface.100" fontSize="md" mb="1" textAlign="center">
                    School
                  </Text>
                  <Box width="100%">
                    <Select
                      bg="surface.100"
                      borderColor="surface.200"
                      borderRadius="2xl"
                      fontSize="lg"
                      onValueChange={(itemValue) => {
                        dispatchState({
                          input: "selectedSchoolIdOnModal",
                          value: itemValue,
                        });
                      }}
                      p="3"
                      placeholder="Select school"
                      selectedValue={state.values.selectedSchoolIdOnModal}
                      textAlign="center"
                    >
                      {teacherSchools?.map((item) => {
                        if (!item?.id) {
                          return null;
                        }

                        return (
                          <Select.Item
                            key={item?.id}
                            actionSheetLabel={titleCaseConverter(item.name) ?? ""}
                            value={item?.id}
                          />
                        );
                      }) ??
                        allSchools.map((item) => {
                          if (!item?.node?.id) {
                            return null;
                          }

                          return (
                            <Select.Item
                              key={item.node.id}
                              actionSheetLabel={titleCaseConverter(item.node.name) ?? ""}
                              value={item.node.id}
                            />
                          );
                        })}
                    </Select>
                  </Box>
                </VStack>
                <ButtonDebounced
                  _text={{ fontSize: "xl" }}
                  height="56px"
                  isDisabled={!state.values.selectedSchoolIdOnModal}
                  mb="4"
                  mt="8"
                  onPress={() => {
                    dispatchState({
                      input: "selectSchoolModalIsOpen",
                      value: false,
                    });
                    dispatchState({
                      input: "addLessonBlockModalIsOpen",
                      value: true,
                    });
                  }}
                  width="160px"
                >
                  {"Continue"}
                </ButtonDebounced>
              </VStack>
            </Modal.Body>
          </Modal.Content>
        </Modal>
        <CreateBreakBlockModal
          addNewLessonBlock={addNewLessonBlock}
          addNewLessonBlockInFlight={addNewLessonBlockInFlight}
          block={state.values.block}
          dayIndex={(state.values.dayIndex ?? 0) + 1}
          hours={state.values.hours ?? 0}
          minutes={state.values.minutes ?? 0}
          schoolId={state.values.schoolId ?? ""}
          setShowModal={(isHidden) =>
            dispatchState({
              input: "addBreakBlockModalIsOpen",
              value: isHidden,
            })
          }
          showModal={state.values.addBreakBlockModalIsOpen ?? false}
          startingYear={state.values.startingYear}
        />
        <Actionsheet
          animationType="fade"
          hideDragIndicator
          isOpen={optionsIsOpen}
          justifyContent="center"
          onClose={optionsOnClose}
          size="lg"
        >
          <Actionsheet.Content
            alignItems="center"
            closeButtonProps={{ right: "5", top: "5" }}
            justifyContent="center"
            mx="auto"
            roundedBottom={12}
            roundedTop={12}
            showCloseButton
            w="40%"
          >
            <Text alignSelf="center" fontSize="xl" fontWeight="bold" py="4">
              Lesson Block Options
            </Text>
            <Actionsheet.Item
              _text={{ fontSize: "lg" }}
              leftIcon={
                <Center size="7">
                  <TrashIcon color="surface.900" size="6" />
                </Center>
              }
              onPress={() => {
                if (state.values.lessonBlockActionsheetId) {
                  removeLessonBlocks(
                    [state.values.lessonBlockActionsheetId],
                    state.values.lessonBlocksConnectionId
                  );
                }
                optionsOnClose();
              }}
            >
              Delete lesson block
            </Actionsheet.Item>
          </Actionsheet.Content>
        </Actionsheet>
        <Actionsheet
          animationType="fade"
          hideDragIndicator
          isOpen={changeStatusActionsheetIsOpen}
          justifyContent="center"
          onClose={changeStatusActionsheetOnClose}
          size="lg"
        >
          <Actionsheet.Content
            alignItems="center"
            closeButtonProps={{ right: "5", top: "5" }}
            justifyContent="center"
            mx="auto"
            roundedBottom={12}
            roundedTop={12}
            showCloseButton
            w="40%"
          >
            <Text alignSelf="center" fontSize="xl" fontWeight="bold" py="4">
              Update Account Status
            </Text>
            <Text
              alignSelf="center"
              fontFamily="Poppins-Regular"
              fontSize="md"
              mx="8"
              pb="2"
              textAlign="center"
            >
              {`${
                accountData.profile?.status !== "ARCHIVED"
                  ? route.params.accountType === "PARENT"
                    ? "Archiving this account will also archive all of their children's accounts."
                    : route.params.accountType === "PUPIL"
                      ? "Archiving this account will also archive their parent's account (if they don't have any other active children)."
                      : "Only delete an account if it has been created by mistake."
                  : "Only delete an account if it has been created by mistake."
              }`}
            </Text>
            {accountData.profile?.status !== "ARCHIVED" ? (
              <Actionsheet.Item
                _text={{ fontSize: "lg" }}
                leftIcon={
                  <Center size="7">
                    <ArchivedIcon color="surface.600" size="6" />
                  </Center>
                }
                onPress={() => {
                  updateAccountStatus({ status: "ARCHIVED" });
                  changeStatusActionsheetOnClose();
                }}
              >
                Archive account
              </Actionsheet.Item>
            ) : (
              <Actionsheet.Item
                _text={{ fontSize: "lg" }}
                leftIcon={
                  <Center size="7">
                    <TickIcon color="primary.400" size="7" />
                  </Center>
                }
                onPress={() => {
                  updateAccountStatus({ status: "ACTIVE" });
                  changeStatusActionsheetOnClose();
                }}
              >
                Make account active
              </Actionsheet.Item>
            )}
            <Actionsheet.Item
              _text={{ fontSize: "lg" }}
              leftIcon={
                <Center size="7">
                  <TrashIcon color="surface.800" size="6" />
                </Center>
              }
              onPress={() => {
                dispatchState({
                  input: "deleteAccountAlertIsOpen",
                  value: true,
                });
                changeStatusActionsheetOnClose();
              }}
            >
              Delete account
            </Actionsheet.Item>
          </Actionsheet.Content>
        </Actionsheet>
        <AlertPopup
          alertIsOpen={Boolean(state.values.leaveAlertAction)}
          body={`You have made unsaved changes to ${
            accountData.profile?.user.firstName
              ? accountData.profile?.user.firstName + "'s"
              : "these"
          } registers.`}
          header="Save your changes?"
          safeButtonRef={safeLeaveRef}
          setAlertIsOpen={(isOpen: boolean) =>
            dispatchState({
              input: "leaveAlertAction",
              value: isOpen,
            })
          }
        >
          <Button
            _text={{ fontSize: "lg" }}
            colorScheme="red"
            minWidth="100"
            onPress={() => {
              const leaveAlertAction = state.values.leaveAlertAction;
              if (isNavigationAction(leaveAlertAction)) {
                navigation.dispatch(leaveAlertAction);
              } else {
                leaveAlertAction?.();
              }
              dispatchState({
                input: "leaveAlertAction",
                value: undefined,
              });
              registerUpdates.current = [];
              dispatchState({
                input: "registerUpdater",
                value: Math.random(),
              });
            }}
            width="40%"
          >
            Discard
          </Button>
          <ButtonDebounced
            ref={safeLeaveRef}
            _text={{ fontSize: "lg" }}
            colorScheme="primary"
            minWidth="100"
            onPress={() => {
              dispatchState({
                input: "leaveAlertAction",
                value: undefined,
              });
              updateRegisters(registerUpdates.current, () => {
                const leaveAlertAction = state.values.leaveAlertAction;
                if (isNavigationAction(leaveAlertAction)) {
                  navigation.dispatch(leaveAlertAction);
                } else {
                  leaveAlertAction?.();
                }
              });
            }}
            width="40%"
          >
            Save
          </ButtonDebounced>
        </AlertPopup>
        <AlertPopup
          alertIsOpen={Boolean(state.values.removeParentRelationshipAlertIsOpen)}
          body="This does not delete either account."
          header={`Are you sure you want to remove ${
            route.params.accountType === "PARENT"
              ? "this parents' relationship with this child?"
              : "this childs' relationship with this parent?"
          }`}
          safeButtonRef={safeRemoveRelationshipRef}
          setAlertIsOpen={(isOpen: boolean) =>
            dispatchState({
              input: "removeParentRelationshipAlertIsOpen",
              value: isOpen,
            })
          }
        >
          <Button
            _text={{ fontSize: "lg" }}
            colorScheme="muted"
            minWidth="100"
            onPress={() => {
              dispatchState({
                input: "clickedAccountId",
                value: undefined,
              });
              dispatchState({
                input: "removeParentRelationshipAlertIsOpen",
                value: false,
              });
            }}
            width="40%"
          >
            No, go back
          </Button>
          <ButtonDebounced
            ref={safeRemoveRelationshipRef}
            _text={{ fontSize: "lg" }}
            colorScheme="primary"
            minWidth="100"
            onPress={() => {
              if (state.values.clickedAccountId) {
                removeParentRelationship({
                  pupilId: state.values.clickedAccountId,
                });
                dispatchState({
                  input: "removeParentRelationshipAlertIsOpen",
                  value: false,
                });
              }
            }}
            width="40%"
          >
            Yes, remove it
          </ButtonDebounced>
        </AlertPopup>
        <AlertPopup
          alertIsOpen={Boolean(state.values.deleteAccountAlertIsOpen)}
          body="This cannot be undone and all their data will be permanently lost."
          header={`Are you sure you want to delete this ${route.params.accountType.toLowerCase()}?`}
          safeButtonRef={safeDeleteAccountRef}
          setAlertIsOpen={(isOpen: boolean) =>
            dispatchState({
              input: "deleteAccountAlertIsOpen",
              value: isOpen,
            })
          }
        >
          <Button
            ref={safeDeleteAccountRef}
            _text={{ fontSize: "lg" }}
            colorScheme="muted"
            minWidth="100"
            onPress={() => {
              dispatchState({
                input: "deleteAccountAlertIsOpen",
                value: false,
              });
            }}
            width="40%"
          >
            No, go back
          </Button>
          <ButtonDebounced
            _text={{ fontSize: "lg" }}
            colorScheme="red"
            minWidth="100"
            onPress={() => {
              updateAccountStatus({ status: "DELETE" });
              dispatchState({
                input: "deleteAccountAlertIsOpen",
                value: false,
              });
            }}
            width="40%"
          >
            Yes, delete
          </ButtonDebounced>
        </AlertPopup>
      </>
    );
  }, [
    accountData.profile?.dbsExpirationDate,
    accountData.profile?.status,
    accountData.profile?.user.firstName,
    accountType,
    addNewLessonBlock,
    addNewLessonBlockInFlight,
    allSchools,
    changeStatusActionsheetIsOpen,
    changeStatusActionsheetOnClose,
    childrenConnections,
    dispatchState,
    editProfile,
    navigation,
    optionsIsOpen,
    optionsOnClose,
    removeLessonBlocks,
    removeParentRelationship,
    rescheduleLesson,
    route.params.accountType,
    route.params.profileId,
    state.values.block,
    state.values.clickedAccountId,
    state.values.dateTimePickerModalDate,
    state.values.dateTimePickerModalLessonDuration,
    state.values.dateTimePickerModalLessonId,
    state.values.dayIndex,
    state.values.dbsExpiryDatePickerIsOpen,
    state.values.deleteAccountAlertIsOpen,
    state.values.hours,
    state.values.isAddLessonBreakModal,
    state.values.leaveAlertAction,
    state.values.lessonBlockActionsheetId,
    state.values.lessonBlocksConnectionId,
    state.values.minutes,
    state.values.pupilsToArchiveModalInfo,
    state.values.removeParentRelationshipAlertIsOpen,
    state.values.schoolId,
    state.values.selectSchoolModalIsOpen,
    state.values.selectedSchoolIdOnModal,
    state.values.addBreakBlockModalIsOpen,
    state.values.addLessonBlockModalIsOpen,
    state.values.createAccountModalIsOpen,
    state.values.startingYear,
    state.values.updateBlockDetailsModalInfo?.keepFree,
    state.values.updateBlockDetailsModalInfo?.lessonBlockCursor,
    state.values.updateBlockDetailsModalInfo?.lessonBlockDataForPupilChange,
    state.values.updateBlockDetailsModalInfo?.lessonBlockIds,
    state.values.updateBlockDetailsModalInfo?.onUpdate,
    state.values.updateBlockDetailsModalInfo?.schoolId,
    state.values.updateBlockDetailsModalInfo?.selectedProfileInfo,
    state.values.updateBlockDetailsModalInfo?.type,
    teacherSchools,
    updateAccountStatus,
    updateLessonBlockDetails,
    updateLessonBlockDetailsInFlight,
    updateRegisters,
    updateScheduledLessonBlockTime,
    updateScheduledLessonBlockTimeInFlight,
  ]);

  const renderSaveButton = useMemo(() => {
    return (
      <PresenceTransition
        animate={{
          opacity: 1,
          scale: 1,
          transition: {
            duration: 250,
          },
        }}
        initial={{
          opacity: 0,
          scale: 0,
        }}
        visible={registerUpdates.current.length > 0}
      >
        <Button
          _focus={{ opacity: 1 }}
          _hover={{ opacity: 1 }}
          _pressed={{ opacity: 1 }}
          _spinner={{ size: "lg" }}
          _text={{ fontSize: "xl" }}
          alignSelf="center"
          bottom="5"
          height="60px"
          isLoading={updateRegistersInFlight}
          leftIcon={<CompleteIcon size="8" />}
          onPress={() => updateRegisters(registerUpdates.current)}
          position="absolute"
          shadow={5}
          width="300px"
        >
          Save
        </Button>
      </PresenceTransition>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateRegisters, updateRegistersInFlight, state.values.registerUpdater]);

  return (
    <Box flex={1}>
      {renderSideBar}
      <Box
        flex={1}
        flexGrow={1}
        ml={state.values.sideBarIsCollapsed ? SIDE_BAR_COLLAPSED_WIDTH : SIDE_BAR_EXPANDED_WIDTH}
        mt="70"
        pt="6"
        px="6"
      >
        <TopTabBar
          bg="primary.100"
          borderRadius="lg"
          buttonStyle={{
            _hover: { bg: "primary.200" },
            _pressed: { bg: "primary.300" },
            alignItems: "center",
            bg: "primary.50",
            pb: 2,
            pt: 2,
          }}
          containerProps={{ pt: 4 }}
          currentPageIndex={state.values.currentPageIndex ?? 0}
          hideBottomBar
          mb="6"
          overflow="hidden"
          pages={pages}
          selectedButtonStyle={{ bg: "primary.100" }}
          selectedTextStyle={{ color: "primary.600" }}
          setCurrentPageIndex={(index) => {
            dispatchState({
              input: "currentPageIndex",
              value: index,
            });
            navigation.setParams({ currentPageIndex: index });
          }}
          shadow={1}
          textStyle={{ color: "primary.800" }}
          width="100%"
        />
        {renderTab}
        {renderSaveButton}
      </Box>
      {renderModals}
    </Box>
  );
};

const AccountScreen: FC<ScreenProps> = (props) => {
  const { navigation, route } = props;

  const [defaultStartingYear, startingYears] = useMemo(
    () => getDefaultStartingYearAndStartingYearsForSelect(),
    []
  );

  const { user } = useAuth();

  const initialState = useMemo(() => {
    let dayIndex = route.params?.dayIndex !== undefined ? Number(route.params?.dayIndex) : 0;
    // select the current day on the teacher register tab
    if (route.params?.accountType === "TEACHER" && route.params?.dayIndex === undefined) {
      // adjust to mon = 0, sun = 6 day indexing
      dayIndex = (new Date().getDay() + 6) % 7;
    }
    let initialStartingYear = route.params?.startingYear;
    if (initialStartingYear === undefined) {
      initialStartingYear = user?.profile?.profileGroup?.currentStartingYear ?? defaultStartingYear;
      navigation.setParams({
        startingYear: initialStartingYear,
      });
    }
    let initialBlock = route.params?.block;
    if (initialBlock === undefined) {
      if (
        user?.profile?.profileGroup?.currentStartingYear &&
        user?.profile?.profileGroup?.currentStartingYear > defaultStartingYear
      ) {
        initialBlock = 1;
      } else {
        initialBlock = user?.profile?.currentBlock ?? getCurrentBlock();
      }
      navigation.setParams({
        block: initialBlock,
      });
    }
    return {
      values: {
        block: Number(initialBlock),
        contentIsRendered: false,
        currentPageIndex: Number(route.params?.currentPageIndex ?? 0),
        dayIndex,
        isRefetching: false,
        registerUpdater: 0,
        schoolId: route.params?.schoolId,
        searchTerm: route.params?.searchTerm ?? "",
        startingYear: Number(initialStartingYear),
        teachingDays: [],
        timetableDate: route.params?.timetableDate
          ? new Date(route.params?.timetableDate)
          : new Date(),
        timetableView: route.params?.timetableView ?? "WEEK",
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const [loadAccountQueryReference, loadAccountQuery] =
    useQueryLoader<LoadAccountQuery>(load_account);

  useEffect(() => {
    if (!loadAccountQueryReference) {
      loadAccountQuery(
        {
          block: state.values.block,
          profileId: route.params.profileId,
          startingYear: state.values.startingYear,
        },
        { fetchPolicy: "store-or-network" }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAccountQuery, route.params.profileId]);

  useEffect(() => {
    navigation.setParams({
      dayIndex: state.values.dayIndex,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.dayIndex]);

  const renderLoadingFallback = useMemo(() => {
    return (
      <HStack flex={1}>
        <SideBar
          isCollapsed={state.values.sideBarIsCollapsed}
          navigation={navigation}
          position="relative"
          setIsCollapsed={() =>
            dispatchState({
              input: "sideBarIsCollapsed",
              value: !state.values.sideBarIsCollapsed,
            })
          }
          top="0"
          width={
            state.values.sideBarIsCollapsed ? SIDE_BAR_COLLAPSED_WIDTH : SIDE_BAR_EXPANDED_WIDTH
          }
        />
        <LoadingBlobs
          ml={
            state.values.sideBarIsCollapsed
              ? SIDE_BAR_COLLAPSED_WIDTH + 1
              : SIDE_BAR_EXPANDED_WIDTH + 1
          }
          pt="70"
        >
          Loading Account...
        </LoadingBlobs>
      </HStack>
    );
  }, [navigation, state.values.sideBarIsCollapsed]);

  return (
    <Box bg="surface.100" flex={1}>
      {loadAccountQueryReference ? (
        <Suspense fallback={renderLoadingFallback}>
          <AccountContent
            accountType={route.params.accountType}
            dispatchState={dispatchState}
            loadAccountQueryReference={loadAccountQueryReference}
            navigation={navigation}
            route={route}
            startingYears={startingYears}
            state={state}
          />
        </Suspense>
      ) : (
        renderLoadingFallback
      )}
    </Box>
  );
};

export const screenOptions = {
  headerTitle: "Account",
};

export default AccountScreen;
